import { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchEnvironment } from '@/store/slices/envsSlice';
import webApiAdmin from '@/lib/api-common/webApiAdmin';
import { withRetry, isTransientHttpError } from '@/lib/utils/retry';
import { connectSocket, disconnectSocket } from '@/lib/socket';
import {
  getStoredNotificationSocketEnabled,
  NOTIFICATION_SOCKET_PREF_CHANGE_EVENT,
} from '@/lib/utils/notificationSocketPref';

const SESSION_KEEPALIVE_INTERVAL_MS = 5 * 60 * 1000;
const MAX_CONSECUTIVE_FAILURES = 3;

/**
 * 앱 마운트 시 환경/인증 정보 로드
 *
 * 참고: 스테이징 배포 환경에서 CSRF 세션 선요청(/login)을 강제하면
 * 로그인 페이지 진입 시 불필요한 auth 요청이 발생한다.
 * 초기 로드는 getEnvironment만 수행하고, 실제 인증 시점(로그인 submit)에서 처리한다.
 */
export function AuthInit({ children }: { children: React.ReactNode }) {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((s) => s.envs.currentUser);

  useEffect(() => {
    let cancelled = false;
    function init() {
      if (!cancelled) {
        void dispatch(fetchEnvironment());
      }
    }
    init();
    return () => { cancelled = true; };
  }, [dispatch]);

  // User-configurable opt-out (checkbox in the notification dropdown) so
  // people who don't actively use realtime notifications can stop the
  // recurring Socket.IO connection/polling entirely.
  const [socketPrefEnabled, setSocketPrefEnabled] = useState(getStoredNotificationSocketEnabled);

  useEffect(() => {
    const handler = (e: Event) => {
      setSocketPrefEnabled((e as CustomEvent<boolean>).detail);
    };
    window.addEventListener(NOTIFICATION_SOCKET_PREF_CHANGE_EVENT, handler);
    return () => window.removeEventListener(NOTIFICATION_SOCKET_PREF_CHANGE_EVENT, handler);
  }, []);

  // Open the realtime socket only while authenticated AND the user hasn't
  // opted out; close it otherwise so an unauthenticated/expired session or a
  // disabled preference never keeps polling the socket transport.
  useEffect(() => {
    if (currentUser && socketPrefEnabled) {
      connectSocket();
    } else {
      disconnectSocket();
    }
  }, [currentUser, socketPrefEnabled]);

  const failureCountRef = useRef(0);

  useEffect(() => {
    if (!currentUser) {
      failureCountRef.current = 0;
      return;
    }

    const keepAlive = async () => {
      try {
        await withRetry(
          () => webApiAdmin.post('admin/campaign/environment/get', {}),
          {
            maxRetries: 2,
            baseDelay: 2000,
            maxDelay: 8000,
            shouldRetry: isTransientHttpError,
          },
        );
        // 성공 시 연속 실패 카운터 리셋
        failureCountRef.current = 0;
      } catch {
        failureCountRef.current += 1;
        if (import.meta.env.DEV) {
          console.warn(
            `[keepalive] consecutive failures: ${failureCountRef.current}/${MAX_CONSECUTIVE_FAILURES}`,
          );
        }
        // 연속 N회 이상 실패하면 인터셉터의 401 핸들러에 맡김 (자동 로그아웃)
        // 그 전까지는 다음 인터벌에서 자연 복구를 기대
      }
    };

    const intervalId = window.setInterval(() => void keepAlive(), SESSION_KEEPALIVE_INTERVAL_MS);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [currentUser]);

  return <>{children}</>;
}
